import type { StaffMe, Thread, ThreadKind } from '@central/api';

/** The placeholders a canned reply body may hold, with what each becomes. */
export const placeholders = [
  { key: 'customer.name', help: 'the customer' },
  { key: 'ticket.ref', help: 'the ticket reference, e.g. GC-1042' },
  { key: 'ticket.subject', help: 'the ticket subject' },
  { key: 'reporter.name', help: 'who reported the ticket' },
  { key: 'me.name', help: 'your name' },
] as const;

export type Placeholder = (typeof placeholders)[number]['key'];

export type PlaceholderValues = Partial<Record<Placeholder, string>>;

/** The thread kinds a canned reply may apply to, as the inbox labels them. */
export const cannedKindOptions: Array<{ value: ThreadKind; label: string }> = [
  { value: 'question', label: 'Question' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'license', label: 'License change' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
  { value: 'crash', label: 'Crash report' },
];

export const cannedKindLabel = (k?: ThreadKind) =>
  k ? (cannedKindOptions.find((o) => o.value === k)?.label ?? k) : 'Every kind';

const token = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;

/**
 * body with each {{placeholder}} replaced by its value. An unknown
 * placeholder, or a known one with no value for this thread, stays as typed
 * so the writer sees what is left to fill in.
 */
export function renderCannedReply(body: string, values: PlaceholderValues): string {
  return body.replace(token, (whole, key: string) => {
    const v = values[key as Placeholder];
    return v ? v : whole;
  });
}

/**
 * The placeholder values for thread t written by me. A crash report's
 * reporter is the system, so reporter.name has no value there.
 */
export function threadValues(t: Thread, me: StaffMe): PlaceholderValues {
  return {
    'customer.name': t.customer.name,
    'ticket.ref': t.ref,
    'ticket.subject': t.subject,
    'reporter.name': t.reportedBy.kind === 'system' ? undefined : t.reportedBy.name,
    'me.name': me.user.name,
  };
}

/**
 * value with text put in place of the selection [start, end), and the caret
 * just after it. Without a selection, text is appended as a new paragraph.
 */
export function insertText(
  value: string,
  text: string,
  start?: number | null,
  end?: number | null,
): { value: string; caret: number } {
  if (start == null) {
    const sep = value === '' || value.endsWith('\n\n') ? '' : value.endsWith('\n') ? '\n' : '\n\n';
    const next = value + sep + text;
    return { value: next, caret: next.length };
  }
  const s = Math.max(0, Math.min(start, value.length));
  const e = Math.max(s, Math.min(end ?? s, value.length));
  return { value: value.slice(0, s) + text + value.slice(e), caret: s + text.length };
}
